import React, { useEffect, useState } from 'react';
import {
  InfluencerLink,
  PARTNER_ROLE_NAME,
  PlansENUM,
  UserItemProps
} from '../index';
import { Form } from '../../../../components';
import { useStateReducer } from '../../../../hooks';
import {
  InstructionWrapper,
  TextField,
  ToggleSwitch,
  Checkbox
} from '../../../../components/fields';
import Heading from '../../../../_global/components/heading/Heading';
import './_styles.scss';
import {
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  capitalize
} from '@mui/material';
import { roleDataService } from '../../../roles/services';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';
import userDataService from './../../services/dataService';
import {
  IGetRequestLimit,
  IRequestLimitStateObject
} from '../../../../interfaces';
import Select from 'react-select';
import { partnerCategoryOptions } from '../edit-item/EditItem';

export default function ViewItem({
  data: {
    _id,
    email_address,
    first_name,
    last_name,
    source,
    admin_right,
    awayz_access,
    user_roles = [],
    influencer_links = [],
    userRegion,
    partner_category
  }
}: UserItemProps) {
  const [state, setState] = useStateReducer({
    roles: [],
    influencer_link: [],
    awayz_remaining_calls: null,
    bilt_remaining_calls: null,
    bilt_number_of_calls: null
  });
  const [selectedRegion, setSelectedRegion] = useState<string>();
  const displayName = `
    ${first_name ?? ''} ${last_name ? last_name + ' | ' : ''} ${
    email_address ?? ''
  }
  `.trim();

  useEffect(() => {
    initData();
  }, []);
  const getRequestLimitArgs: IGetRequestLimit = { _id: _id };

  const initData = async () => {
    const rolesData = await roleDataService.getRoles();
    setState({ roles: rolesData });

    await Promise.all([
      userDataService.getAwayzUserRequestLimit(getRequestLimitArgs),
      userDataService.getBiltUserRequestLimit(getRequestLimitArgs)
    ]).then(async ([awayzRequestLimitResponse, biltRequestLimitResponse]) => {
      if (awayzRequestLimitResponse && awayzRequestLimitResponse.length) {
        const awayzStateEntry: IRequestLimitStateObject = {};
        for (const limit of awayzRequestLimitResponse) {
          awayzStateEntry[limit.type] = {
            remaining_calls: limit.remaining_calls,
            number_of_calls: limit.number_of_calls,
            search_limit: limit.search_limit,
            is_admin: limit.is_admin
          };
        }
        setState({
          awayz_remaining_calls: awayzStateEntry,
          bilt_remaining_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.remaining_calls
            : 0,
          bilt_number_of_calls: biltRequestLimitResponse
            ? biltRequestLimitResponse.number_of_calls
            : 0
        });
      }
    });

    if (userRegion) {
      setSelectedRegion(userRegion);
    }
  };

  const getAwayzAccessValue = () => {
    return awayz_access && awayz_access.awayz_forever_enum
      ? awayz_access.awayz_forever_enum
      : PlansENUM.free;
  };

  const primary_partner_category = partnerCategoryOptions.find(
    (i: any) => i.value === partner_category?.primary
  );
  const secondary_partner_category = partnerCategoryOptions.find(
    (i: any) => i.value === partner_category?.secondary
  );

  return (
    <div id='user-view-item' className='read-only'>
      <Heading name={displayName} />
      <Form title='Personal Details'>
        <InstructionWrapper text='Accounts first and last name.' row>
          <TextField label='First Name' value={first_name} readOnly />
          <TextField label='Last Name' value={last_name} readOnly />
        </InstructionWrapper>
        <InstructionWrapper
          text='Accounts email address and source by registration.'
          row
        >
          <TextField label='Email Address' value={email_address} readOnly />
          {securityService.checkRoleAccess(UserPermissions.viewUserSource) ? (
            <TextField label='Source' value={source} readOnly />
          ) : (
            <p></p>
          )}
        </InstructionWrapper>
      </Form>

      {securityService.checkRoleAccess(UserPermissions.viewUserAwayzAccess) ? (
        <Form title='Environment Access (Awayz.com)'>
          <InstructionWrapper
            text={"'Stripe Access' is linked to payments"}
            row
          >
            <FormLabel>
              Stripe Access
              <div style={{ margin: '14px 0px' }}>
                {capitalize(
                  awayz_access?.awayz_enum
                    ? awayz_access?.awayz_enum
                    : 'Unknown'
                )}
              </div>
            </FormLabel>
            <FormLabel>
              Admin Portal
              <InstructionWrapper row>
                <ToggleSwitch
                  label={admin_right ? 'Yes' : 'No'}
                  className={'display-block'}
                  name={'awayz_access'}
                  value={admin_right}
                  readOnly
                />
              </InstructionWrapper>
            </FormLabel>
          </InstructionWrapper>

          <InstructionWrapper
            text={
              "'Manual Access' to override 'Stripe Access' \n Reason: demote or promote user "
            }
            row
          >
            <FormLabel>
              Manual Access
              <RadioGroup
                row
                aria-labelledby='awayz-environment-access-label'
                defaultValue={getAwayzAccessValue}
                name='awayz-access-radio-buttons-group'
              >
                <FormControlLabel
                  value={PlansENUM.free}
                  control={<Radio />}
                  label='None'
                  disabled
                />
                <FormControlLabel
                  value={PlansENUM.gold}
                  control={<Radio />}
                  label='Awayz Gold'
                  disabled
                />
                <FormControlLabel
                  value={PlansENUM.platinum}
                  control={<Radio />}
                  label='Awayz Platinum'
                  disabled
                />
              </RadioGroup>
            </FormLabel>
          </InstructionWrapper>
        </Form>
      ) : null}

      <Form title='User Region'>
        <InstructionWrapper text={'Select the user region'} row>
          <FormControl>
            <Select
              id='user_region'
              value={{ label: userRegion ? userRegion : 'No region selected' }}
              placeholder='Please select a region'
              options={[]}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  padding: '0.5rem',
                  fontSize: '1rem',
                  borderRadius: '5px',
                  border: '1px solid #ccc',
                  color: 'white !important',
                  backgroundColor: '#0000001A',
                  margin: '12px 0px'
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? '#0000003A'
                    : 'transparent',
                  color: state.isSelected ? 'white' : 'black',
                  ':hover': {
                    backgroundColor: '#0000003A',
                    color: 'white'
                  }
                }),
                singleValue: (provided, state) => ({
                  ...provided,
                  color: 'white'
                })
              }}
              isDisabled={true}
            />
          </FormControl>
        </InstructionWrapper>
      </Form>

      <Heading name='Request Limit' />

      <Form title='Awayz Remaining calls'>
        <InstructionWrapper row className='request-limit-wrapper'>
          {state.awayz_remaining_calls ? (
            <FormControlLabel
              style={{ margin: '0rem' }}
              control={<Checkbox label='Hotel' disabled />}
              label={
                state.awayz_remaining_calls &&
                state.awayz_remaining_calls.Hotel &&
                state.awayz_remaining_calls.Hotel.remaining_calls !== 'Infinity'
                  ? `${state.awayz_remaining_calls.Hotel.remaining_calls} / ${
                      state.awayz_remaining_calls.Hotel.number_of_calls +
                      state.awayz_remaining_calls.Hotel.remaining_calls
                    }`
                  : state.awayz_remaining_calls.Hotel.remaining_calls
              }
            />
          ) : null}

          {state.awayz_remaining_calls ? (
            <FormControlLabel
              style={{ margin: '0rem' }}
              control={<Checkbox label='Flights' disabled />}
              label={
                state.awayz_remaining_calls &&
                state.awayz_remaining_calls.Flight &&
                state.awayz_remaining_calls.Flight.remaining_calls !==
                  'Infinity'
                  ? `${state.awayz_remaining_calls.Flight.remaining_calls} / ${
                      state.awayz_remaining_calls.Flight.number_of_calls +
                      state.awayz_remaining_calls.Flight.remaining_calls
                    }`
                  : state.awayz_remaining_calls.Flight.remaining_calls
              }
            />
          ) : null}
        </InstructionWrapper>
      </Form>
      <Form title='Bilt Remaining Calls'>
        <InstructionWrapper>
          {state.bilt_remaining_calls ? (
            <FormControlLabel
              control={<Checkbox label='Bilt.awayz.com' disabled />}
              style={{ margin: '0rem' }}
              label={
                state.bilt_remaining_calls &&
                state.bilt_remaining_calls !== 'Infinity'
                  ? `${state.bilt_remaining_calls} / ${
                      state.bilt_number_of_calls + state.bilt_remaining_calls
                    }`
                  : state.bilt_remaining_calls
              }
            />
          ) : null}
        </InstructionWrapper>
      </Form>

      <Form>
        <Heading style={{ margin: '12px 0px' }} name='Influencer Links' />

        {influencer_links.map(
          (influencer_link: InfluencerLink, index: number) => {
            return (
              <div key={'influencer_link_' + index}>
                {
                  <Card className='influencer-link-card-view page-padding'>
                    <TextField
                      value={index + 1}
                      style={{ width: '40px' }}
                      readOnly
                    ></TextField>

                    <TextField
                      readOnly
                      className='next-each-other'
                      label={'Code'}
                      value={influencer_link.code}
                    ></TextField>

                    <TextField
                      readOnly
                      className='next-each-other'
                      label={'Url'}
                      value={influencer_link.url}
                    ></TextField>
                  </Card>
                }
              </div>
            );
          }
        )}
      </Form>

      <Form>
        <Heading name='Account Roles'></Heading>
        {state.roles?.map((role: any, index: number) => {
          return role.is_active || state.roles.includes(role._id) ? (
            <div key={'user_roles_card_' + index}>
              <InstructionWrapper row text={role.rolename + ' Role'}>
                <Card className='program-card-edit page-padding'>
                  {
                    <div key={role.rolename + index} className='flex-row'>
                      <div className='label-col'>
                        <TextField value={role.rolename} readOnly></TextField>
                      </div>

                      <div className='switch-col'>
                        <ToggleSwitch
                          label=''
                          name={role._id}
                          value={user_roles.includes(role._id)}
                          readOnly={true}
                        />
                      </div>
                    </div>
                  }
                </Card>
              </InstructionWrapper>
            </div>
          ) : null;
        })}
      </Form>
      {user_roles?.includes(
        state.roles?.find((i: any) => i.rolename === PARTNER_ROLE_NAME)?._id
      ) && (
        <Form title='Partner Label'>
          <InstructionWrapper
            text={"Partner's primary and secondary label"}
            row
          >
            <FormControl>
              <Select
                id='partner_primary_category'
                defaultValue={primary_partner_category}
                placeholder='Please select a category'
                isDisabled={true}
                aria-errormessage={
                  state.partner_primary_category
                    ? 'please select a category'
                    : ''
                }
                options={partnerCategoryOptions}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    padding: '0.5rem',
                    fontSize: '1rem',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    color: 'white !important',
                    backgroundColor: '#0000001A',
                    margin: '12px 0px'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#0000003A'
                      : 'transparent',
                    color: state.isSelected ? 'white' : 'black',
                    ':hover': {
                      backgroundColor: '#0000003A',
                      color: 'white'
                    }
                  }),
                  singleValue: (provided, state) => ({
                    ...provided,
                    color: 'white'
                  })
                }}
              />
              <Select
                id='partner_secondary_category'
                defaultValue={secondary_partner_category}
                placeholder='Please select a category'
                isDisabled={true}
                options={partnerCategoryOptions}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: '0.5rem',
                    fontSize: '1rem',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    color: 'white !important',
                    backgroundColor: '#0000001A',
                    margin: '12px 0px'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#0000003A'
                      : 'transparent',
                    color: state.isSelected ? 'white' : 'black',
                    ':hover': {
                      backgroundColor: '#0000003A',
                      color: 'white'
                    }
                  }),
                  singleValue: (provided, state) => ({
                    ...provided,
                    color: 'white'
                  })
                }}
              />
            </FormControl>
          </InstructionWrapper>
        </Form>
      )}
    </div>
  );
}
